import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabaseRpc } from '@/lib/supabaseClient'
import { resolveAssetUrl } from '@/lib/assetUrl'
import { isRealtimeEnabled } from './subscriptions'

export type DropStatus = 'active' | 'closed' | 'expired'

export type UiDrop = {
  id: string
  organizationId: string
  name: string
  description?: string
  status: DropStatus
  createdAt: Date
  opensAt: Date
  closesAt: Date
  timeLimitHours: number
  retentionExpiresAt?: Date
  isTrial: boolean
  coverPhotoUrl?: string
  photoCount: number
  contributorCount: number
  viewCount: number
  defaultContributorToken?: string
}

type DropRow = {
  id: string
  organization_id?: string
  organizationId?: string
  name: string
  description?: string | null
  status: DropStatus
  created_at?: string
  createdAt?: string
  opens_at?: string
  opensAt?: string
  closes_at?: string
  closesAt?: string
  time_limit_hours?: number
  timeLimitHours?: number
  retention_expires_at?: string | null
  retentionExpiresAt?: string | null
  is_trial?: boolean | null
  isTrial?: boolean | null
  cover_photo_url?: string | null
  coverPhotoUrl?: string | null
  photo_count?: number | null
  photoCount?: number | null
  contributor_count?: number | null
  contributorCount?: number | null
  view_count?: number | null
  viewCount?: number | null
  default_contributor_token?: string | null
  defaultContributorToken?: string | null
  photos_aggregate?: { aggregate?: { count?: number | null } | null } | null
  contributors_aggregate?: { aggregate?: { count?: number | null } | null } | null
  usage_stats?: { link_views?: number | null } | null
  contributors?: Array<{ token?: string | null }>
}

function parseDate(value: string | undefined, field: string): Date {
  if (!value) throw new Error(`Missing date from API: ${field}`)
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) throw new Error(`Invalid date from API: ${value}`)
  return d
}

export function toUiDrop(row: DropRow): UiDrop {
  return {
    id: row.id,
    organizationId: row.organization_id ?? row.organizationId ?? '',
    name: row.name,
    description: row.description || undefined,
    status: row.status,
    createdAt: parseDate(row.created_at ?? row.createdAt, 'created_at'),
    opensAt: parseDate(row.opens_at ?? row.opensAt, 'opens_at'),
    closesAt: parseDate(row.closes_at ?? row.closesAt, 'closes_at'),
    timeLimitHours: row.time_limit_hours ?? row.timeLimitHours ?? 0,
    retentionExpiresAt: row.retention_expires_at || row.retentionExpiresAt
      ? parseDate(row.retention_expires_at ?? row.retentionExpiresAt ?? undefined, 'retention_expires_at')
      : undefined,
    isTrial: !!(row.is_trial ?? row.isTrial),
    coverPhotoUrl: resolveAssetUrl(row.cover_photo_url ?? row.coverPhotoUrl),
    photoCount: row.photo_count ?? row.photoCount ?? row.photos_aggregate?.aggregate?.count ?? 0,
    contributorCount: row.contributor_count ?? row.contributorCount ?? row.contributors_aggregate?.aggregate?.count ?? 0,
    viewCount: row.view_count ?? row.viewCount ?? row.usage_stats?.link_views ?? 0,
    defaultContributorToken:
      row.default_contributor_token ?? row.defaultContributorToken ?? row.contributors?.[0]?.token ?? undefined,
  }
}

export function useDropsQuery() {
  const query = useQuery({
    queryKey: ['drops'],
    queryFn: () => supabaseRpc<DropRow[]>('list_my_drops'),
    refetchInterval: 30_000,
  })

  const drops = (query.data || []).map(toUiDrop)
  return { ...query, drops }
}

export function useDropQuery(id: string | undefined) {
  const query = useQuery({
    queryKey: ['drop', id],
    queryFn: () => supabaseRpc<DropRow | DropRow[] | null>('get_drop_detail', { p_drop_id: id }),
    enabled: !!id,
  })

  const row = Array.isArray(query.data) ? query.data[0] ?? null : query.data
  const drop = row ? toUiDrop(row) : null

  return { ...query, drop }
}

export function useDeleteDropMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => supabaseRpc('delete_drop', { p_drop_id: id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drops'] })
    },
  })
}

export function useCreateDropMutation() {
  const queryClient = useQueryClient()

  const create = async (params: {
    organizationId: string
    name: string
    description?: string
    timeLimitHours: number
    coverPhotoUrl?: string
    isTrial?: boolean
  }) => {
    const rawResult = await supabaseRpc<Array<{
      drop_id?: string
      dropId?: string
      created_drop_id?: string
      contributor_token?: string
      contributorToken?: string
    }> | {
      drop_id?: string
      dropId?: string
      created_drop_id?: string
      contributor_token?: string
      contributorToken?: string
    }>('create_drop', {
      p_organization_id: params.organizationId,
      p_name: params.name,
      p_description: params.description ?? null,
      p_time_limit_hours: params.timeLimitHours,
      p_cover_photo_url: params.coverPhotoUrl ?? null,
      p_is_trial: params.isTrial ?? false,
    })

    const result = Array.isArray(rawResult) ? rawResult[0] : rawResult
    const dropId = result?.created_drop_id ?? result?.drop_id ?? result?.dropId
    if (!dropId) throw new Error('Failed to create drop')

    queryClient.invalidateQueries({ queryKey: ['drops'] })
    return {
      dropId,
      contributorToken: result.contributor_token ?? result.contributorToken,
    }
  }

  return { create }
}

export type UiPhoto = {
  id: string
  dropId: string
  contributorId: string
  originalUrl: string
  thumbnailUrl?: string
  caption?: string
  fileSizeBytes: number
  fileHash: string
  uploadedAt: string
  processingStatus: string
  processedUrl?: string
  width?: number
  height?: number
  processedSizeBytes?: number
  exifData?: Record<string, unknown>
}

type PhotoRow = {
  id: string
  drop_id?: string
  dropId?: string
  contributor_id?: string
  contributorId?: string
  original_url?: string
  originalUrl?: string
  thumbnail_url?: string | null
  thumbnailUrl?: string | null
  caption?: string | null
  file_size_bytes?: number
  fileSizeBytes?: number
  file_hash?: string
  fileHash?: string
  uploaded_at?: string
  uploadedAt?: string
  processing_status?: string | null
  processingStatus?: string | null
  processed_url?: string | null
  processedUrl?: string | null
  width?: number | null
  height?: number | null
  processed_size_bytes?: number | null
  processedSizeBytes?: number | null
  exif_data?: Record<string, unknown> | null
  exifData?: Record<string, unknown> | null
}

function toUiPhoto(row: PhotoRow): UiPhoto {
  return {
    id: row.id,
    dropId: row.drop_id ?? row.dropId ?? '',
    contributorId: row.contributor_id ?? row.contributorId ?? '',
    originalUrl: resolveAssetUrl(row.original_url ?? row.originalUrl) ?? '',
    thumbnailUrl: resolveAssetUrl(row.thumbnail_url ?? row.thumbnailUrl),
    caption: row.caption || undefined,
    fileSizeBytes: row.file_size_bytes ?? row.fileSizeBytes ?? 0,
    fileHash: row.file_hash ?? row.fileHash ?? '',
    uploadedAt: row.uploaded_at ?? row.uploadedAt ?? '',
    processingStatus: row.processing_status ?? row.processingStatus ?? 'pending',
    processedUrl: resolveAssetUrl(row.processed_url ?? row.processedUrl),
    width: row.width ?? undefined,
    height: row.height ?? undefined,
    processedSizeBytes: row.processed_size_bytes ?? row.processedSizeBytes ?? undefined,
    exifData: row.exif_data ?? row.exifData ?? undefined,
  }
}

export function useDropPhotosQuery(dropId: string | undefined) {
  const query = useQuery({
    queryKey: ['drop-photos', dropId],
    queryFn: () => supabaseRpc<PhotoRow[]>('list_drop_photos', { p_drop_id: dropId }),
    enabled: !!dropId,
    refetchInterval: isRealtimeEnabled() ? false : 30_000,
  })

  const photos = (query.data || []).map(toUiPhoto)
  return { ...query, photos }
}

export function useUpdateDropMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; set: Record<string, unknown> }) =>
      supabaseRpc('update_drop', { p_drop_id: vars.id, p_patch: vars.set }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drops'] })
      queryClient.invalidateQueries({ queryKey: ['drop'] })
    },
  })
}
